import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { toPairs } from 'lodash'
import { Heading } from '@ideon-api/ideon-ui'
import { networkTierType } from '../../BenefitsSet/Helpers/tierTypeFactory'

// PD-30615 MR3: settled 2026-09-18 (PD-30615 comment) -- Deductibles and
// MOOPs are the only two groups shaped as a cross-product (individual/family
// x medical/drug) rather than an unrelated list, and they are the two groups
// Ops checks first. Matched by group name, not hardcoded to medical, so this
// generalizes to any product line whose benefitGroups.json happens to define
// a group with this name -- today, only Medical's does.
const MATRIX_GROUP_NAMES = ['Deductibles', 'MOOPs']
const MATRIX_ROWS = ['individual', 'family']
const MATRIX_COLS = ['medical', 'drug']

const GROUP_HEADING_CLASS = 'text-sm mb-2'
const COLUMN_GRID_CLASS = 'grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3'
const MATRIX_GRID_CLASS = 'grid grid-cols-2 gap-x-6 gap-y-3'

export class PlanBenefitsComponent extends Component {
  static propTypes = {
    plan: PropTypes.object.isRequired,
    fieldGroups: PropTypes.object.isRequired
  }

  get fields() {
    return this.props.fieldGroups.Benefits.benefits.fields
  }

  get dentalMetadataFields() {
    return {
      coverage_tier_basic: 'Coverage Tier: Basic',
      coverage_tier_preventive: 'Coverage Tier: Preventive',
      coverage_tier_major: 'Coverage Tier: Major',
      adult_individual_deductible: 'Deductible: Adult Individual',
      child_individual_deductible: 'Deductible: Child Individual',
      family_deductible: 'Deductible: Family',
      adult_individual_annual_max: 'Annual Max: Adult Individual',
      child_individual_annual_max: 'Annual Max: Child Individual',
      child_individual_moop: 'MOOP: Child Individual'
    }
  }

  benefitFields = (fieldGroup) => {
    const fieldNames = {}

    fieldGroup.fields.forEach(
      (field) => { fieldNames[field.vericred_name || field.name] = field.label }
    )

    return fieldNames
  }

  // Dental's metadata rows (coverage tiers, deductibles, MOOPs) live on
  // `plan.metadata`, not `plan.benefits`, and carry no group of their own --
  // merged into whichever group renders, same as before this change.
  fieldsAndBenefitsForGroup = (plan, fieldGroup) => {
    let fields = this.benefitFields(fieldGroup)
    let benefits = plan.benefits

    if (plan.product_line === 'dental') {
      fields = { ...this.dentalMetadataFields, ...fields }
      benefits = { ...this.dentalMetadataValues(plan.metadata), ...benefits }
    }

    return { fields, benefits }
  }

  dentalMetadataValues = (metadata) => {
    return {
      coverage_tier_basic: metadata.coverage_tiers.basic,
      coverage_tier_preventive: metadata.coverage_tiers.preventive,
      coverage_tier_major: metadata.coverage_tiers.major,
      adult_individual_deductible: metadata.adult_individual_deductible,
      child_individual_deductible: metadata.child_individual_deductible,
      family_deductible: metadata.family_deductible,
      adult_individual_annual_max: metadata.adult_individual_annual_max,
      child_individual_annual_max: metadata.child_individual_annual_max,
      child_individual_moop: metadata.child_individual_moop
    }
  }

  isMatrixGroup = (fieldGroup) => MATRIX_GROUP_NAMES.includes(fieldGroup.name)

  matrixCellField = (fieldGroup, row, col) => fieldGroup.fields.find((f) => f.name.startsWith(`${row}_${col}_`))

  renderMatrix = (plan, fieldGroup, tierType) => {
    const { benefits } = this.fieldsAndBenefitsForGroup(plan, fieldGroup)
    const cells = []

    MATRIX_ROWS.forEach((row) => {
      MATRIX_COLS.forEach((col) => {
        const field = this.matrixCellField(fieldGroup, row, col)
        if (!field) return

        cells.push(
          <div key={`${plan.id}-${field.name}`}>
            {tierType.cvtTableRow(plan.id, field.name, field.label, benefits[field.name])}
          </div>
        )
      })
    })

    return <div className={MATRIX_GRID_CLASS}>{cells}</div>
  }

  renderColumns = (plan, fieldGroup, tierType) => {
    const { fields, benefits } = this.fieldsAndBenefitsForGroup(plan, fieldGroup)

    return (
      <div className={COLUMN_GRID_CLASS}>
        {toPairs(fields).map(([fieldName, benefitName]) => (
          <div key={`${plan.id}-${fieldName}`}>
            {tierType.cvtTableRow(plan.id, fieldName, benefitName, benefits[fieldName])}
          </div>
        ))}
      </div>
    )
  }

  fieldGroupSection = (plan, fieldGroup) => {
    const tierType = fieldGroup.tier || networkTierType

    return (
      <div key={`${plan.id}-${fieldGroup.name}`} className="mb-6" data-testid={`benefit-group-${fieldGroup.name}`}>
        <Heading variant="title-m" className={GROUP_HEADING_CLASS}>{fieldGroup.name}</Heading>
        {this.isMatrixGroup(fieldGroup)
          ? this.renderMatrix(plan, fieldGroup, tierType)
          : this.renderColumns(plan, fieldGroup, tierType)}
      </div>
    )
  }

  render() {
    const { plan } = this.props

    return (
      <div id={`${plan.id}-benefits`}>
        {this.fields.map((fieldGroup) => this.fieldGroupSection(plan, fieldGroup))}
      </div>
    )
  }
}

export default PlanBenefitsComponent
